import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { ptlEnvs } from "@/tra/tancis/ptli/comn";
import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */
export const BASE = {
    path: `${ptlEnvs.base}/btch/excutnrslt`,
    api: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/btch/excutn-rslts`,
    nodes: [],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    ptli1204001q: `${BASE.path}/ptli1204001q`, // Managing batch runs Result list !== 배치실행결과 목록 ==!
    ptli1204002q: `${BASE.path}/ptli1204002q`, // Managing batch runs Result update !== 배치실행결과 수정 ==!
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    //!== 배치 실행 결과 목록을 조회한다.==!
    getBtchExcutnRsltList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    //!== 배치 실행 결과 상세를 조회한다.==!
    getBtchExcutnRsltInfo: (jobExecutionId: any, bsopClsfCd: any) => {
        const data = {
            bsopClsfCd,
        };

        return api.get(`${BASE.api}/${jobExecutionId}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    //!== 배치 실행 결과 내용을 수정한다.==!
    updateBtchExcutnRslt: (data: any) => {
        return api.post(`${BASE.api}`, {
            ...data,
        });
    },
};

export const SF_BTCH_EXCUTN_RSLT_SEARCH_FORM: TFormSchema = {
    id: "form_excutnSrch",
    schema: {
        /* Business Type !== 업무분류 ==!*/
        bsopClsfCd: {
            type: "select",
            label: "L_BSOP_CLSF_NM",
            all: false,
            edit: true,
            required: true,
            area: "comnCd",
            comnCd: "COM0024",
            excludes: ["TDW", "TDA"],
        },
        status: {
            type: "select",
            label: "L_STATUS",
            edit: true,
            options: [
                { label: "STARTED", value: "STARTED" },
                { label: "FAILED", value: "FAILED" },
                { label: "COMPLETED", value: "COMPLETED" },
            ],
        },
        startTimeRnge: {
            type: "daterange",
            label: "L_RGSR_PRID",
            start: { name: "startTimeStart" },
            end: { name: "startTimeEnd" },
            rangeButton: 4,
        } /* Start Time Range !== 시작시간 범위 ==!*/,
        jobName: {
            type: "text",
            label: "L_JOB_NM",
            edit: true,
        } /* Job Name !== job 이름(배치명) ==!*/,
    },
};

export const SF_BTCH_EXCUTN_RSLT_FORM: TFormSchema = {
    id: "form_excutnRslt",
    schema: {
        jobExecutionId: {
            type: "text",
            label: "L_JOB_EXECUTION_ID",
            edit: false,
        } /* jobExecutionId !== Job 실행 ID ==!*/,
        jobInstanceId: {
            type: "text",
            label: "L_JOB_INSTANCE_ID",
            edit: false,
        } /* jobInstanceId !== Job 인스턴스 ID ==!*/,
        version: { type: "text", label: "L_VER", edit: false } /* version !== 버전 ==!*/,
        startTime: { type: "datetime", label: "L_STRT_TIME", edit: false } /* startTime !== 수행일시 ==!*/,
        endTime: { type: "datetime", label: "L_END_TIME", edit: false } /* endTime !== 종료일시 ==!*/,
        status: { type: "text", label: "L_STAT", edit: false } /* status !== 상태 ==!*/,
        exitCode: { type: "text", label: "L_EXIT_CD", edit: false } /* exitCode !== 수행결과코드 ==!*/,
        exitMessage: {
            type: "textarea",
            label: "L_EXIT_MSG",
            edit: true,
            readOnly: true,
            rows: 5,
        } /* exitMessage !== 수행결과메세지 ==!*/,
        lastUpdated: { type: "datetime", label: "L_LAST_MDFY_DTM", edit: false } /* lastUpdated !== 최종수정시점 ==!*/,
    },
};

export const SG_BTCH_EXCUTN_RSLT_LIST: TGridSchema = {
    id: "grid_btchExcutnRsltList",
    options: { pagination: "out", edit: false, checkbox: false, radio: false, index: true },
    head: [
        { cells: [{ header: "L_BSOP_CLSF_NM", binding: "bsopClsfCd", width: "*" }], id: "bsopClsfCd", show: false },
        { cells: [{ header: "L_JOB_NM", binding: "jobName", width: "*" }], id: "jobName" },
        { cells: [{ header: "L_JOB_INSTANCE_ID", binding: "jobInstanceId", width: "10%" }], id: "jobInstanceId" },
        { cells: [{ header: "L_JOB_EXECUTION_ID", binding: "jobExecutionId", width: "10%" }], id: "jobExecutionId" },
        { cells: [{ header: "L_VER", binding: "version", width: "7%" }], id: "version" },
        { cells: [{ header: "L_STRT_TIME", binding: "startTime", width: "15%" }], id: "startTime" },
        { cells: [{ header: "L_END_TIME", binding: "endTime", width: "15%" }], id: "endTime" },
        { cells: [{ header: "L_PRID", binding: "duringTime", width: "15%" }], id: "duringTime" },
        { cells: [{ header: "L_STAT", binding: "status", width: "10%" }], id: "status" },
    ],
    body: [
        {
            cells: [{ binding: "bsopClsfCd" }],
        },
        {
            cells: [{ binding: "jobName" }],
        },
        {
            cells: [{ binding: "jobInstanceId" }],
        },
        {
            cells: [{ binding: "jobExecutionId" }],
        },
        {
            cells: [{ binding: "version" }],
        },
        {
            cells: [{ binding: "startTime" }],
        },
        {
            cells: [{ binding: "endTime" }],
        },
        {
            cells: [{ binding: "duringTime" }],
        },
        {
            cells: [{ binding: "status" }],
        },
    ],
};

export const SG_BTCH_EXCUTN_RSLT_STEP_LIST: TGridSchema = {
    id: "grid_btchExcutnRsltList",
    // options: { pagination: "out", edit: false, checkbox: false, radio: false, index: false },
    head: [
        { cells: [{ header: "L_STEP_EXECUTION_ID", binding: "stepExecutionId", width: "*" }], id: "stepExecutionId" },
        { cells: [{ header: "L_VER", binding: "version", width: "*" }], id: "version" },
        { cells: [{ header: "L_STEP_NM", binding: "stepName", width: "*" }], id: "stepName" },
        { cells: [{ header: "L_STRT_TIME", binding: "startTime", width: "*" }], id: "startTime" },
        { cells: [{ header: "L_END_TIME", binding: "endTime", width: "*" }], id: "endTime" },
        { cells: [{ header: "L_STAT", binding: "status", width: "*" }], id: "status" },
        { cells: [{ header: "L_COMMIT", binding: "commitCount", width: "*" }], id: "commitCount" },
        { cells: [{ header: "L_READ", binding: "readCount", width: "*" }], id: "readCount" },
        { cells: [{ header: "L_FILTER", binding: "filterCount", width: "*" }], id: "filterCount" },
        { cells: [{ header: "L_WRITE", binding: "writeCount", width: "*" }], id: "writeCount" },
        { cells: [{ header: "L_READ_SKIP", binding: "readSkipCount", width: "*" }], id: "readSkipCount" },
        { cells: [{ header: "L_WRITE_SKIP", binding: "writeSkipCount", width: "*" }], id: "writeSkipCount" },
        { cells: [{ header: "L_PROCESS_SKIP", binding: "processSkipCount", width: "*" }], id: "processSkipCount" },
        { cells: [{ header: "L_ROLLBACK", binding: "rollbackCount", width: "*" }], id: "rollbackCount" },
        { cells: [{ header: "L_EXIT_CD", binding: "exitCode", width: "*" }], id: "exitCode" },
        { cells: [{ header: "T_SRCH", binding: "srch", width: "*" }], id: "srch" },
    ],
    body: [
        { cells: [{ binding: "stepExecutionId" }] },
        { cells: [{ binding: "version" }] },
        { cells: [{ binding: "stepName" }] },
        { cells: [{ binding: "startTime", type: "datetime" }] },
        { cells: [{ binding: "endTime", type: "datetime" }] },
        { cells: [{ binding: "status" }] },
        { cells: [{ binding: "commitCount" }] },
        { cells: [{ binding: "readCount" }] },
        { cells: [{ binding: "filterCount" }] },
        { cells: [{ binding: "writeCount" }] },
        { cells: [{ binding: "readSkipCount" }] },
        { cells: [{ binding: "writeSkipCount" }] },
        { cells: [{ binding: "processSkipCount" }] },
        { cells: [{ binding: "rollbackCount" }] },
        { cells: [{ binding: "exitCode" }] },
        { cells: [{ binding: "srch" }] },
    ],
};
